import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api/api.service';
import { environment } from '@environment/environment';

declare class MaxxChat {
  constructor(config: Record<string, unknown>);
  destroy(): void;
}

export interface MaxxSettingsTarget {
  tab: string;
  edit: string;
}

export interface MaxxSdkConfig {
  containerId: string;
  agentName?: string;
  isEssentials?: boolean;
  mode?: 'full' | 'sidebar';
  referrer?: string;
  initialPrompt?: string;
  onClose?: () => void;
  onOpenSettings?: (target?: MaxxSettingsTarget) => void;
  chatEndpoint?: string;
  suggestedPrompts?: string[];
  headerTitle?: string;
}

const MAX_INIT_RETRIES = 50;
const RETRY_INTERVAL_MS = 100;

@Injectable({ providedIn: 'root' })
export class MaxxSdkService {
  private sdkLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(private apiService: ApiService) {}

  async loadSdk(): Promise<void> {
    if (this.sdkLoaded) return;
    if (!this.loadingPromise) {
      this.loadingPromise = this.performSdkLoad();
    }
    return this.loadingPromise;
  }

  private async performSdkLoad(): Promise<void> {
    try {
      await Promise.all([
        this.loadAsset('link', `${environment.MAXX_FRONTEND_URL}/maxx-chat.css`),
        this.loadAsset('script', `${environment.MAXX_FRONTEND_URL}/maxx-chat.js`),
      ]);
      this.sdkLoaded = true;
    } catch (error) {
      this.loadingPromise = null;
      console.error('Failed to load MaxxChat SDK:', error);
    }
  }

  createInstance(config: MaxxSdkConfig): Promise<MaxxChat> {
    return new Promise((resolve, reject) => {
      let retriesLeft = MAX_INIT_RETRIES;

      const tryInit = (): void => {
        const MaxxChatConstructor = (window as unknown as Record<string, unknown>)['MaxxChat'] as
          | typeof MaxxChat
          | undefined;

        if (MaxxChatConstructor) {
          const instance = new MaxxChatConstructor({
            container: `#${config.containerId}`,
            apiBaseUrl: environment.MAXX_API_URL,
            fronteggAuthToken: this.apiService.jwtToken,
            tenantId: this.apiService.shopId.toString(),
            agentName: config.agentName ?? 'EXTERNAL_ANALYST_AGENT',
            isEssentials: config.isEssentials ?? false,
            ...(config.mode && { mode: config.mode }),
            ...(config.referrer && { referrer: config.referrer }),
            ...(config.initialPrompt && { initialPrompt: config.initialPrompt }),
            ...(config.onClose && { onClose: config.onClose }),
            ...(config.onOpenSettings && { onOpenSettings: config.onOpenSettings }),
            ...(config.chatEndpoint && { chatEndpoint: config.chatEndpoint }),
            ...(config.suggestedPrompts && { suggestedPrompts: config.suggestedPrompts }),
            ...(config.headerTitle && { headerTitle: config.headerTitle }),
            ...(environment.POSTHOG_API_KEY
              ? { posthog: { apiKey: environment.POSTHOG_API_KEY, host: environment.POSTHOG_HOST } }
              : {}),
          });
          resolve(instance);
        } else if (retriesLeft > 0) {
          retriesLeft--;
          setTimeout(tryInit, RETRY_INTERVAL_MS);
        } else {
          reject(new Error('Failed to initialize MaxxChat SDK after maximum retries'));
        }
      };

      tryInit();
    });
  }

  destroyInstance(instance: unknown): void {
    if (instance && typeof instance === 'object' && 'destroy' in instance) {
      (instance as MaxxChat).destroy();
    }
  }

  private loadAsset(type: 'link' | 'script', url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const selector = type === 'link' ? `link[href="${url}"]` : `script[src="${url}"]`;
      if (document.querySelector(selector)) {
        resolve();
        return;
      }

      const element = document.createElement(type);
      if (type === 'link') {
        (element as HTMLLinkElement).rel = 'stylesheet';
        (element as HTMLLinkElement).href = url;
      } else {
        (element as HTMLScriptElement).src = url;
        (element as HTMLScriptElement).async = true;
      }
      element.onload = () => resolve();
      element.onerror = () => reject(new Error(`Failed to load ${type}: ${url}`));
      document.head.appendChild(element);
    });
  }
}
